import './styles.css'

import { useCallback, useContext, useEffect, useRef } from 'react'

import { Box, HStack, Icon, Popover, StackSeparator, useDisclosure } from '@chakra-ui/react'
import {
  CubeBackIcon,
  CubeDownIcon,
  CubeFrontIcon,
  CubeLeftIcon,
  CubeRightIcon,
  CubeUpIcon,
} from '@editor/assets/icons'
import { EDITOR_TOOLS, EDITOR_TOOL_BUTTON_SIZE } from '@editor/config/constants'
import { EditorContext } from '@editor/contexts/Editor'
import { CameraViewOrientation } from '@editor/interfaces/camera'
import ToolbarButton from '@editor/pages/projects/editor/toolbar/components/ToolbarButton'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import * as Sentry from '@sentry/react'
import { BiCamera } from 'react-icons/bi'
import { BsGearFill } from 'react-icons/bs'
import { PiWarningCircleFill } from 'react-icons/pi'
import { useLocation } from 'react-router'

import CameraFocusButton from '../../../CameraFocus/components/CameraFocusButton'
import { CameraProfileTarget } from '../../definitions'
import { setProfileTarget } from '../../store'
import CameraViewButton from '../CameraViewButton'
import EditProfileIcon from '../EditProfileIcon'
import FocusItemButton from '../FocusItemButton'

/**
 * Timeout for the camera profile sub-toolbar to close.
 * Value is in milliseconds.
 */
const CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT = 3 * 1000

/**
 * Camera sub-toolbar timeout.
 */
let subToolbarTimeout: NodeJS.Timeout

export const CameraToolbar = () => {
  // Popover toolbar
  const { open, onOpen, onClose } = useDisclosure()
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const shouldAnimate = urlParams.get('ca') !== 'false'

  // Context
  const { changeTool, cameraRef, arcballControlsRef } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const cameraProfile = useAppSelector((state) => state.toolCameraProfile.cameraProfile)
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)
  const isEditingAreaProfile = useAppSelector(
    (state) =>
      state.toolCameraProfile.profileTarget === CameraProfileTarget.Area &&
      state.editor.selectedTool === EDITOR_TOOLS.CAMERA_PROFILE,
  )
  const focusedItem = useAppSelector((state) =>
    state.inspectionItems.items.find((item) => item.inspection_item_id === state.toolUtil.core.focusedElementId),
  )

  // Refs
  const cameraProfileButtonRef = useRef<HTMLDivElement>(null)

  /**
   * Re-orient camera to saved profile.
   * @param orientation Camera view orientation.
   * @param noAutoClose Flag to not auto close the camera profile sub-toolbar.
   */
  const orientCameraToProfile = useCallback(
    async (orientation: CameraViewOrientation, noAutoClose?: boolean) => {
      if (cameraProfile && arcballControlsRef.current && cameraRef.current) {
        await arcballControlsRef.current.animateToProfile(cameraProfile, orientation, shouldAnimate ? undefined : 0)
      }
      if (!noAutoClose) onClose()
    },
    [cameraProfile, cameraRef, arcballControlsRef, shouldAnimate, onClose],
  )

  /**
   * Clicking the main view button will orient the camera to the front view
   * as well as open the camera profile sub-toolbar.
   */
  const onClickMainView = useCallback(async () => {
    onOpen()
    try {
      await orientCameraToProfile(CameraViewOrientation.FRONT, true)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          tool: 'camera-profile',
          action: 'orientCameraToProfile',
        },
      })
    } finally {
      clearTimeout(subToolbarTimeout)
      subToolbarTimeout = setTimeout(() => {
        onClose()
      }, CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT)
    }
  }, [onOpen, onClose, orientCameraToProfile])

  /**
   * Switch to edit mode when the camera profile button is clicked in the sub-toolbar.
   */
  const switchEditMode = useCallback(
    (tool: string) => {
      dispatch(setProfileTarget(CameraProfileTarget.Area))
      changeTool(tool)
    },
    [changeTool, dispatch],
  )

  /**
   * Close sub-toolbar when the selected tool is camera profile.
   */
  useEffect(() => {
    if (isEditingAreaProfile) {
      onClose()
    }
  }, [isEditingAreaProfile, onClose])

  return (
    <HStack
      id="camera-toolbar"
      bottom={2}
      left={2}
      h={EDITOR_TOOL_BUTTON_SIZE}
      position="absolute"
      backgroundColor="gray2.800"
      borderRadius="md"
      gap={0}
      data-testid="camera-assistant-bar"
      separator={<StackSeparator borderColor="whiteAlpha.200" />}
    >
      <CameraFocusButton selectedTool={selectedTool} changeTool={changeTool} />

      {isAllowedToModify && (!cameraProfile || isEditingAreaProfile) && (
        <ToolbarButton
          icon={
            <Box position="relative">
              <BiCamera />
              <Icon position="absolute" top="-2px" right="-2px" width="75%" height="75%" color="black" asChild>
                <BsGearFill />
              </Icon>
              <Icon
                position="absolute"
                top="-2px"
                right="-2px"
                width="65%"
                height="65%"
                color={isEditingAreaProfile ? 'orange' : 'white'}
                asChild
              >
                <BsGearFill />
              </Icon>
              {!cameraProfile && (
                <Icon
                  as={PiWarningCircleFill}
                  position="absolute"
                  top="-70%"
                  right="-70%"
                  width="20px"
                  height="20px"
                  color="orange.400"
                />
              )}
            </Box>
          }
          label="正面を設定"
          toolType={EDITOR_TOOLS.CAMERA_PROFILE}
          buttonSize={EDITOR_TOOL_BUTTON_SIZE}
          selectedTool={selectedTool}
          changeTool={switchEditMode}
          toolTipPlacement="top"
        />
      )}

      {cameraProfile && !isEditingAreaProfile && (
        <Box w={EDITOR_TOOL_BUTTON_SIZE} h={EDITOR_TOOL_BUTTON_SIZE}>
          <CameraViewButton
            ref={cameraProfileButtonRef}
            label="カメラを正面(-y)からの視点に移動"
            icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
            onClick={onClickMainView}
            onLongPress={onOpen}
            hasSubtoolbar
          />
          <Popover.Root
            open={open}
            closeOnInteractOutside
            closeOnEscape
            onOpenChange={(e) => {
              if (!e.open) {
                onClose()
              }
            }}
            positioning={{
              placement: 'top',
              getAnchorRect() {
                return cameraProfileButtonRef.current?.getBoundingClientRect() || null
              },
            }}
          >
            <Popover.Positioner>
              <Popover.Content
                w="fit-content"
                backgroundColor="secondary.800"
                borderColor="secondary.700"
                borderWidth={1}
                borderRadius="md"
                left={10}
              >
                <Popover.Body p={0}>
                  <HStack
                    h={EDITOR_TOOL_BUTTON_SIZE}
                    backgroundColor="gray2.800"
                    borderRadius="md"
                    gap={0}
                    data-testid="camera-profile-view-orientation-bar"
                    separator={<StackSeparator borderColor="whiteAlpha.200" />}
                    p={0}
                  >
                    <CameraViewButton
                      label="カメラを左(-x)からの視点に移動"
                      icon={<CubeLeftIcon width="50%" height="50%" data-testid="cameraFromLeft" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.LEFT)}
                    />

                    <CameraViewButton
                      label="カメラを右(+x)からの視点に移動"
                      icon={<CubeRightIcon width="50%" height="50%" data-testid="cameraFromRight" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.RIGHT)}
                    />
                    <CameraViewButton
                      label="カメラを正面(-y)からの視点に移動"
                      icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.FRONT)}
                    />
                    <CameraViewButton
                      label="カメラを裏(+y)からの視点に移動"
                      icon={<CubeBackIcon width="50%" height="50%" data-testid="cameraFromBack" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.BACK)}
                    />
                    <CameraViewButton
                      label="カメラを下(-z)からの視点に移動"
                      icon={<CubeDownIcon width="50%" height="50%" data-testid="cameraFromBottom" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.BOTTOM)}
                    />
                    <CameraViewButton
                      label="カメラを上(+z)からの視点に移動"
                      icon={<CubeUpIcon width="50%" height="50%" data-testid="cameraFromTop" />}
                      onClick={() => orientCameraToProfile(CameraViewOrientation.TOP)}
                    />
                    {isAllowedToModify && (
                      <ToolbarButton
                        icon={<EditProfileIcon />}
                        label="正面を設定"
                        toolType={EDITOR_TOOLS.CAMERA_PROFILE}
                        buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                        selectedTool={selectedTool}
                        changeTool={switchEditMode}
                        toolTipPlacement="top"
                      />
                    )}
                  </HStack>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Popover.Root>
        </Box>
      )}

      {focusedItem && <FocusItemButton />}
    </HStack>
  )
}
